import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardStore } from '../../stores/dashboardStore';
import { analyticsApi } from '../../services/api';

const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { todayData, fetchTodayData } = useDashboardStore();
  const [historyData, setHistoryData] = useState<any>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchTodayData();
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await analyticsApi.history(dateRange.startDate, dateRange.endDate);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const maxHourlyValue = todayData ? Math.max(...todayData.hourlyDistribution, 1) : 1;

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('navigation.analytics')}
        </h1>
      </div>

      {/* 今日統計 */}
      {todayData && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.analytics.todayStats')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{todayData.totalIssued}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.totalIssued')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-green-500">{todayData.completed}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.completed')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-blue-500">{todayData.avgWaitMinutes}{t('queue.common.minutes')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.avgWait')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-red-500">{todayData.noShowRate}%</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.noShowRate')}</p>
            </div>
          </div>

          {/* 每小時分布圖（簡易 bar chart） */}
          <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">{t('queue.analytics.hourlyDistribution')}</h3>
          <div className="flex items-end gap-1 h-32">
            {todayData.hourlyDistribution.map((count, hour) => (
              <div key={hour} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-brand-500 rounded-t"
                  style={{ height: `${(count / maxHourlyValue) * 100}%`, minHeight: count > 0 ? '4px' : '0' }}
                />
                {hour % 3 === 0 && (
                  <span className="text-xs text-gray-400 mt-1">{hour}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 歷史統計 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.analytics.historyStats')}</h2>

        {/* 日期範圍選擇 */}
        <div className="flex gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.analytics.startDate')}</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.analytics.endDate')}</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchHistory}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {t('common.search')}
            </button>
          </div>
        </div>

        {historyData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{historyData.totalIssued}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.totalIssued')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-green-500">{historyData.completed}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.completed')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-blue-500">{historyData.avgWaitMinutes}{t('queue.common.minutes')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.avgWait')}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-3xl font-bold text-red-500">{historyData.noShowRate}%</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('queue.analytics.noShowRate')}</p>
            </div>
          </div>
        )}

        {/* 來源分布 */}
        {historyData?.sourceDistribution && (
          <div>
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">{t('queue.analytics.sourceDistribution')}</h3>
            <div className="flex gap-4">
              <div className="flex-1 text-center p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-xl font-bold text-gray-700 dark:text-gray-300">{historyData.sourceDistribution.ONSITE}</p>
                <p className="text-sm text-gray-500">ONSITE</p>
              </div>
              <div className="flex-1 text-center p-3 bg-brand-50 dark:bg-brand-900 rounded-lg">
                <p className="text-xl font-bold text-brand-600 dark:text-brand-400">{historyData.sourceDistribution.ONLINE}</p>
                <p className="text-sm text-gray-500">ONLINE</p>
              </div>
              <div className="flex-1 text-center p-3 bg-green-50 dark:bg-green-900 rounded-lg">
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{historyData.sourceDistribution.LINE}</p>
                <p className="text-sm text-gray-500">LINE</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
